import { FunctionComponent, useCallback, useEffect, useState } from "react";

import {
  DefaultButton,
  Link,
  MessageBar,
  MessageBarType,
  Stack,
  TextField
} from "@bps/fluent-ui";

const buildKql = (correlationId: string) => {
  const safeCid = correlationId.trim().replaceAll('"', '\\"');

  return `let cid = "${safeCid}";
traces
| where customDimensions.CorrelationId == cid
| project timestamp, itemId, message
| order by timestamp asc, tostring(itemId) asc
| summarize Messages = make_list(message), Count=count()
| project Count, MessagesJoined = strcat_array(Messages, "")`;
};

export const KqlBuilder: FunctionComponent = () => {
  const [correlationId, setCorrelationId] = useState("");
  const [status, setStatus] = useState<"idle" | "copied" | "error">("idle");

  const tryPasteCorrelationIdFromClipboard = useCallback(() => {
    navigator.clipboard
      .readText()
      .then(text => {
        const trimmed = text.trim();

        if (
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            trimmed
          )
        ) {
          setCorrelationId(current =>
            current === trimmed ? current : trimmed
          );
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    tryPasteCorrelationIdFromClipboard();

    const handleFocus = () => {
      tryPasteCorrelationIdFromClipboard();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        tryPasteCorrelationIdFromClipboard();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [tryPasteCorrelationIdFromClipboard]);

  const handleCopy = async () => {
    if (!correlationId.trim()) {
      setStatus("error");
      return;
    }

    try {
      await navigator.clipboard.writeText(buildKql(correlationId));
      setStatus("copied");
    } catch {
      setStatus("error");
    }
  };

  return (
    <Stack tokens={{ childrenGap: 8 }}>
      <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 12 }} wrap>
        <TextField
          label="Correlation ID"
          value={correlationId}
          placeholder="00000000-0000-0000-0000-000000000000"
          onFocus={tryPasteCorrelationIdFromClipboard}
          onChange={(_, value) => {
            setCorrelationId(value ?? "");
            if (status !== "idle") setStatus("idle");
          }}
          styles={{
            root: { width: 420 }
          }}
        />

        <DefaultButton
          text="Copy KQL"
          onClick={handleCopy}
          styles={{
            root: { minWidth: 120 }
          }}
        />
      </Stack>

      {status === "copied" && (
        <MessageBar messageBarType={MessageBarType.success} isMultiline>
          KQL copied to clipboard. Run the query in{" "}
          <Link
            href="https://portal.azure.com/#@bpcloud.dev/resource/subscriptions/4a693c35-82b1-44e8-92f6-b863f208bfb8/resourceGroups/deva-plt-ae-rg-az/providers/Microsoft.Insights/components/deva-plt-ae-bpid-ai/logs"
            target="_blank"
          >
            DEVA
          </Link>{" "}
          <Link
            href="https://portal.azure.com/#@bpcloud.dev/resource/subscriptions/4a693c35-82b1-44e8-92f6-b863f208bfb8/resourceGroups/uata-plt-ae-rg-az/providers/Microsoft.Insights/components/uata-plt-ae-bpid-ai/logs"
            target="_blank"
          >
            UATA
          </Link>
          <Link
            href="https://portal.azure.com/#@bpcloud.dev/resource/subscriptions/c28a4b39-0bd7-4aca-b78b-6cb9609842ae/resourceGroups/stage-plt-ae-rg-az/providers/Microsoft.Insights/components/stage-plt-ae-bpid-ai/logs"
            target="_blank"
          >
            STAGE
          </Link>
          .
        </MessageBar>
      )}

      {status === "error" && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
          Enter a correlation ID first.
        </MessageBar>
      )}
    </Stack>
  );
};
